/* eslint-disable react-hooks/exhaustive-deps */
import {
  Children,
  forwardRef,
  isValidElement,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type HTMLAttributes,
} from 'react';
import {
  FormControl,
  MenuItem,
  Select,
  FormControlLabel,
  Checkbox as MuiCheckbox,
  Chip,
  Typography,
  Menu,
  Box,
  IconButton,
} from '@mui/material';
import { ExpandLess, ExpandMore, KeyboardArrowDown, MoreVert } from '@mui/icons-material';
import { fontSize } from '../../..';
import { useSelector, useDispatch } from 'react-redux';
import { updateAxesQueryParam } from '../../../redux/ChartPoperties/ChartPropertiesActions';
import {
  setParameterData,
  setParameterIntoFilters,
} from '../../../redux/Parameters/ParametersAction';
import { ParameterProps } from '../ParameterContainer';
import { RootState } from '../../../redux';
import { callQueryAPI } from '../../../redux/ChartPoperties/ChartControlsActions';
import { callQueryAPIDashboard } from '../../../redux/TabTile/TabTileActionsAndMultipleDispatches';
import { AxisType } from '../../CommonFunctions/aliases';
import debounce from 'lodash/debounce';

const MENU_ITEM_HEIGHT = 30;
const MENU_MAX_HEIGHT = 270;
const MENU_VISIBLE_ITEMS = Math.max(1, Math.floor(MENU_MAX_HEIGHT / MENU_ITEM_HEIGHT));
const MENU_OVERSCAN_ITEMS = 8;
const MAX_VIRTUAL_SCROLL_HEIGHT = 8_000_000;

const VirtualizedMenuList = memo(
  forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    function VirtualizedMenuList(props, ref) {
      const { children, ...other } = props;
      const itemData = useMemo(() => Children.toArray(children), [children]);
      const itemCount = itemData.length;
      const viewportHeight = Math.min(itemCount, MENU_VISIBLE_ITEMS) * MENU_ITEM_HEIGHT;
      const totalContentHeight = itemCount * MENU_ITEM_HEIGHT;
      const scaledScrollHeight = Math.min(totalContentHeight, MAX_VIRTUAL_SCROLL_HEIGHT);
      const maxTopIndex = Math.max(0, itemCount - MENU_VISIBLE_ITEMS);
      const [scrollTop, setScrollTop] = useState(0);
      const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
        setScrollTop(event.currentTarget.scrollTop);
      }, []);

      const startIndex = useMemo(() => {
        if (itemCount === 0) return 0;

        const rawIndex =
          totalContentHeight <= MAX_VIRTUAL_SCROLL_HEIGHT
            ? Math.floor(scrollTop / MENU_ITEM_HEIGHT)
            : Math.floor(
                (scrollTop / Math.max(1, scaledScrollHeight - viewportHeight)) * maxTopIndex,
              );

        return Math.max(0, rawIndex - MENU_OVERSCAN_ITEMS);
      }, [
        itemCount,
        maxTopIndex,
        scaledScrollHeight,
        scrollTop,
        totalContentHeight,
        viewportHeight,
      ]);

      const endIndex = Math.min(
        itemCount,
        startIndex + MENU_VISIBLE_ITEMS + MENU_OVERSCAN_ITEMS * 2,
      );

      const visibleItems = itemData.slice(startIndex, endIndex);
      const listStyle = (other.style as CSSProperties) || {};

      if (itemCount === 0) {
        return <div ref={ref} {...other} />;
      }

      return (
        <div
          ref={ref}
          {...other}
          onScroll={handleScroll}
          style={{
            ...listStyle,
            maxHeight: viewportHeight,
            overflowY: 'auto',
            overflowX: 'hidden',
            position: 'relative',
          }}
        >
          <div style={{ height: scaledScrollHeight, position: 'relative' }}>
            <div style={{ position: 'sticky', top: 0, left: 0, right: 0 }}>
              {visibleItems.map((item, index) => (
                <div
                  key={
                    isValidElement(item) && item.key != null
                      ? String(item.key)
                      : `${startIndex + index}`
                  }
                  style={{ height: MENU_ITEM_HEIGHT }}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    },
  ),
);

const Checkbox = ({
  parameter,
  handleEditParameter,
  propKey,
  editMode,
  showDash,
  backgroundColor,
  color,
}: ParameterProps) => {
  const { chartProperties } = useSelector((state: RootState) => {
    return {
      chartProperties: state.chartProperties,
    };
  });
  const dispatch = useDispatch();

  const options = useMemo(() => {
    let opts = [...(parameter.values || [])];

    // If values is empty, generate options from minValue, maxValue, and stepValue
    if (opts.length === 0 && parameter.minValue !== undefined && parameter.maxValue !== undefined) {
      // Handle date type differently
      if (parameter.columns?.dataType === 'date') {
        const minDate = new Date(parameter.minValue);
        const maxDate = new Date(parameter.maxValue);
        const step = Number(parameter.stepValue) || 1; // step in days

        for (let d = new Date(minDate); d <= maxDate; d.setDate(d.getDate() + step)) {
          opts.push(new Date(d).toISOString().split('T')[0]); // Format as YYYY-MM-DD
        }
      } else {
        const minValue =
          typeof parameter.minValue === 'string'
            ? parseFloat(parameter.minValue)
            : parameter.minValue;
        const maxValue =
          typeof parameter.maxValue === 'string'
            ? parseFloat(parameter.maxValue)
            : parameter.maxValue;
        const step =
          typeof parameter.stepValue === 'string'
            ? parseFloat(parameter.stepValue)
            : parameter.stepValue || 1;

        for (let i = minValue; i <= maxValue; i += step) {
          opts.push(i);
        }
      }
    }

    return Array.from(new Set(opts));
  }, [
    parameter.values,
    parameter.columns?.defaultValue,
    parameter.columns?.dataType,
    parameter.minValue,
    parameter.maxValue,
    parameter.stepValue,
  ]);

  const [checkboxValues, setCheckboxValues] = useState(() => {
    let initialValues: any[] = [];
    const defaultVal = parameter.columns.defaultValue;
    if (parameter.userSelection) {
      // Only keep values that exist in options or are special values like '(All)', '(Empty)', '(Blank)'
      const validOptions = [...(options || []), '(All)', '(Empty)', '(Blank)'];
      if (Array.isArray(parameter.userSelection)) {
        const filtered = parameter.userSelection.filter((val: any) => validOptions.includes(val));
        if (filtered.length > 0) {
          initialValues = filtered;
        } else if (defaultVal !== undefined && defaultVal !== null) {
          // Check if defaultVal is within range and of correct type
          let isValidDefault = false;

          if (parameter.columns?.dataType === 'date') {
            const defaultDate = new Date(defaultVal as string);
            const minDate = parameter.minValue ? new Date(parameter.minValue) : null;
            const maxDate = parameter.maxValue ? new Date(parameter.maxValue) : null;

            if (!isNaN(defaultDate.getTime())) {
              isValidDefault = true;
              if (minDate && defaultDate < minDate) isValidDefault = false;
              if (maxDate && defaultDate > maxDate) isValidDefault = false;
            }
          } else if (parameter.columns?.dataType === 'number') {
            const defaultNum = typeof defaultVal === 'string' ? parseFloat(defaultVal) : defaultVal;
            if (!isNaN(defaultNum as number)) {
              isValidDefault = true;
              if (
                parameter.minValue !== undefined &&
                (defaultNum as number) < Number(parameter.minValue)
              ) {
                isValidDefault = false;
              }
              if (
                parameter.maxValue !== undefined &&
                (defaultNum as number) > Number(parameter.maxValue)
              ) {
                isValidDefault = false;
              }
            }
          } else {
            // For string or other types, just check if it exists in values array
            isValidDefault = (parameter.values || []).includes(defaultVal as string);
          }

          if (isValidDefault) {
            initialValues = [defaultVal];
          } else {
            initialValues = [];
          }
        } else {
          initialValues = [];
        }
        // initialValues = filtered.length > 0 ? filtered : defaultVal ? [defaultVal] : [];
      } else {
        initialValues = [parameter.userSelection];
      }
    } else if (parameter.columns?.defaultValue !== undefined) {
      const optionsArray = options || [];

      const isInOptions = optionsArray.some((val: any) => {
        if (parameter.columns?.dataType === 'number') {
          return Number(val) === Number(defaultVal);
        } else if (parameter.columns?.dataType === 'date') {
          return val === defaultVal;
        }
        return val === defaultVal;
      });
      if (isInOptions) {
        initialValues = [defaultVal];
      }
    }
    return initialValues;
  });
  // console.log(options);
  // console.log(checkboxValues);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [expanded, setExpanded] = useState(true);

  const currentChartAxesRef = useRef<any[]>([]);
  const currentChatFieldRef = useRef<any>(null);
  const currentChartAxesNameRef = useRef<any>(null);
  const itemIndexRef = useRef<any>(null);
  const isFirstRender = useRef(true);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const callQueryAPIFilter = () => {
    let callAPIDashboard = false;
    chartProperties.propList[1].forEach((tabTileId: string) => {
      chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
        if (field.parameterId === parameter.parameterId) {
          dispatch(callQueryAPI(tabTileId, true));
          callAPIDashboard = true;
        }
      });
      let measureAxes = chartProperties.properties[tabTileId].chartAxes.filter((axis: any) =>
        [AxisType.Measure, AxisType.X, AxisType.Y].includes(axis.name),
      );
      measureAxes.forEach((axes: any) => {
        axes.fields.some((field: any) => {
          if (field.override) {
            field.override[0].fields.some((FilterField: any) => {
              if (FilterField.parameterId === parameter.parameterId) {
                dispatch(callQueryAPI(tabTileId, true));
                callAPIDashboard = true;
              }
            });
          }
        });
      });
    });
    if (showDash && callAPIDashboard) {
      dispatch(callQueryAPIDashboard(true));
    }
  };

  const updateUserFilterItem = (
    propKey: string,
    bIndex: number,
    itemIndex: number,
    field: any,
    axesName: string,
  ) => {
    dispatch(updateAxesQueryParam(propKey, bIndex, itemIndex, field, axesName));
  };

  const UpdateFilter = useCallback(
    debounce((value: any[]) => {
      // if (currentChatFieldRef.current && currentChatFieldRef.current.isParameterized) {
      //   currentChatFieldRef.current.userSelection = value;

      //   updateUserFilterItem(
      //     propKey,
      //     0,
      //     itemIndexRef.current,
      //     currentChatFieldRef.current,
      //     currentChartAxesNameRef.current,
      //   );
      // }
      Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.forEach((cf: any) => {
          if (!cf.currentChartAxesName?.includes('chartAxes_')) {
            if (cf.propKey) {
              const clonedField = {
                ...chartProperties.properties[cf.propKey].chartAxes[0].fields[cf.itemIndex],
              };
              clonedField.userSelection = value;
              updateUserFilterItem(
                cf.propKey,
                0,
                cf.itemIndex,
                clonedField,
                cf.currentChartAxesName,
              );
            } else if (cf.itemIndex >= 0 && cf.currentChartAxesName) {
              chartProperties.propList[1].forEach((tabTileId: string) => {
                chartProperties.properties[tabTileId].chartAxes[0].fields.some((field: any) => {
                  if (field.parameterId === parameter.parameterId) {
                    dispatch(
                      setParameterIntoFilters(
                        tabTileId,
                        parameter.parameterId,
                        field.uId,
                        cf.itemIndex,
                        cf.currentChartAxesName,
                      ),
                    );

                    const clonedField = {
                      ...chartProperties.properties[tabTileId].chartAxes[0].fields[cf.itemIndex],
                    };
                    clonedField.userSelection = value;
                    updateUserFilterItem(
                      tabTileId,
                      0,
                      cf.itemIndex,
                      clonedField,
                      cf.currentChartAxesName,
                    );
                  }
                });
              });
            }
          }
        }),
        callQueryAPIFilter();
    }, 600),
    [propKey, updateUserFilterItem],
  );

  const handleCheckboxChange = (event: any) => {
    const value = event.target.value;
    const isAllSelected = value.includes('(All)');
    let checkedValues: any[] = [];
    // if "(All)" is selected and not all values are selected, select all
    if (isAllSelected && checkboxValues.length !== options.length + 1) {
      checkedValues = ['(All)', ...options];
    } else if (value.includes('(All)') && value.length < options.length + 1) {
      // if all individual values are selected, add "(All)"
      checkedValues = value.filter((v: any) => v !== '(All)');
    } else if (!isAllSelected && checkboxValues.length === options.length + 1) {
      // if "(All)" is selected and already all selected, deselect all
      checkedValues = [];
    } else if (value.length === options.length && !value.includes('(All)')) {
      // if all individual values are selected, add "(All)"
      checkedValues = ['(All)', ...options];
    } else {
      checkedValues = value;
    }

    setCheckboxValues(checkedValues);
    dispatch(setParameterData(parameter.parameterId, checkedValues));
    checkedValues = checkedValues.filter((v: string) => v !== '(All)');
    UpdateFilter(
      checkedValues.map((v: string) => {
        if (v === '(Empty)') return '';
        if (v === '(Blank)') return null;
        return v;
      }),
    );

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        if (filter.propKey) {
          const chartAxes = chartProperties.properties[filter.propKey]?.chartAxes;
          const chartFilter = chartAxes[chartAxes.length - 1].fields || [];
          chartFilter?.forEach((field: any, index: number) => {
            if (currentChartAxesName_ === field.enableOverrideForUID) {
              return;
            }
            if (field.uId === currentChartAxesName_) {
              const override_ = field.override;
              if (Array.isArray(override_) && override_.length > 0) {
                override_[0].fields.forEach((field_: any, index_: number) => {
                  if (field_.uId === filter.chartFilterId) {
                    field_.userSelection = checkedValues.map((v: string) => {
                      if (v === '(Empty)') return '';
                      if (v === '(Blank)') return null;
                      return v;
                    });
                    field.override = override_;
                    updateUserFilterItem(
                      filter.propKey,
                      chartAxes.length - 1,
                      index,
                      field,
                      'chartAxes',
                    );
                    callQueryAPIFilter();
                  }
                });
              }
            }
          });
        } else {
        }
      }
    });
  };

  useEffect(() => {
    currentChartAxesRef.current = chartProperties.properties[propKey]?.chartAxes[0]?.fields || [];

    // Find the field where any chartFilter in parameter.chartFilter matches filter.uId
    currentChatFieldRef.current = currentChartAxesRef.current.find(
      (filter: any) =>
        Array.isArray(parameter.chartFilter) &&
        parameter.chartFilter.some((cf: any) => {
          if (cf.chartFilterId === filter.uId) {
            currentChartAxesNameRef.current = cf.currentChartAxesName;
            itemIndexRef.current = cf.itemIndex;
            return cf.chartFilterId === filter.uId;
          }
        }),
    );

    if (checkboxValues.length === options.length + 1 && !checkboxValues.includes('(All)')) {
      setCheckboxValues((prev: any) => [...prev, '(All)']);
    }
  }, [chartProperties.properties[propKey]?.chartAxes[0]?.fields, parameter.chartFilter, propKey]);

  // if the parameter had any default value then it will update the Graph
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (
      currentChatFieldRef.current &&
      currentChatFieldRef.current.isParameterized &&
      checkboxValues.length > 0
    ) {
      const validValues = checkboxValues.map((v: string) => {
        if (v === '(Empty)') return '';
        if (v === '(Blank)') return null;
        return v;
      });
      currentChatFieldRef.current.userSelection =
        validValues.filter((v: any) => v !== '(All)') || [];
      updateUserFilterItem(
        propKey,
        0,
        itemIndexRef.current,
        currentChatFieldRef.current,
        currentChartAxesNameRef.current,
      );
      callQueryAPIFilter();
    }

    parameter?.chartFilter?.forEach((filter: any) => {
      if (filter?.currentChartAxesName?.includes('chartAxes_')) {
        const currentChartAxesName_ = filter?.currentChartAxesName.split('_')[1];
        const chartAxes = chartProperties.properties[propKey]?.chartAxes;
        const chartFilter = chartAxes[chartAxes.length - 1].fields;
        chartFilter?.forEach((field: any, index: number) => {
          if (currentChartAxesName_ === field.enableOverrideForUID) {
            return;
          }
          if (field.uId === currentChartAxesName_) {
            const override_ = field.override;
            if (Array.isArray(override_) && override_.length > 0) {
              override_[0].fields.forEach((field_: any, index_: number) => {
                if (field_.uId === filter.chartFilterId) {
                  field_.userSelection = checkboxValues.map((v: string) => {
                    if (v === '(Empty)') return '';
                    if (v === '(Blank)') return null;
                    return v;
                  });
                  field.override = override_;
                  updateUserFilterItem(propKey, chartAxes.length - 1, index, field, 'chartAxes');
                  callQueryAPIFilter();
                }
              });
            }
          }
        });
      }
    });
  }, [
    parameter.chartFilter,
    // currentChatFieldRef.current,
    // propKey,
    // currentChartField?.parameterId
  ]);

  return (
    <Box sx={{ width: '100%', position: 'relative' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography
            variant="body2"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              transform: 'capitalize',
              color: color,
              textAlign: 'start',
              marginLeft: '8px',
            }}
          >
            {parameter?.name}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: -1,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          <IconButton
            size="small"
            id="basic-button"
            aria-controls={open ? 'basic-menu' : undefined}
            sx={{ color: color, transform: 'translateX(6px)' }}
            onClick={handleClick}
          >
            <MoreVert />
          </IconButton>
          <IconButton
            size="small"
            sx={{
              mr: '0.4rem',
              color: color,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <MenuItem dense onClick={() => handleEditParameter(parameter.parameterId)}>
          Remove
        </MenuItem>
      </Menu>

      {expanded && (
        <FormControl fullWidth variant="outlined" size="small" sx={{ width: '94%', ml: '2px' }}>
          <Select
            multiple
            value={checkboxValues}
            onChange={handleCheckboxChange}
            displayEmpty
            style={{
              color: color,
              minHeight: '1rem',
              fontSize: fontSize.medium,
              textAlign: 'left',
            }}
            IconComponent={KeyboardArrowDown}
            renderValue={(selected) => {
              const chips = (selected as string[]).filter((v) => v !== '(All)');
              if ((selected as string[]).length === 0) {
                return (
                  <Typography
                    sx={{
                      color: color,
                      fontSize: fontSize.medium,
                    }}
                  >
                    Select options...
                  </Typography>
                );
              }
              return (
                <div
                  key={parameter.parameterId}
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '5px',
                    maxHeight: '5.2rem',
                    overflowY: 'auto',
                  }}
                >
                  {chips.map((value) => (
                    <ChipDisplay
                      value={value === '' ? '(Empty)' : value == null ? '(Blank)' : value}
                      parameter={parameter}
                    />
                  ))}
                </div>
              );
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 270,
                  maxWidth: 30,
                  overflowY: 'hidden',
                  boxShadow: '0 5px 10px rgba(0,0,0,0.3)',
                  marginTop: '5px',
                  marginLeft: '8px',
                },
              },
              MenuListProps: {
                component: VirtualizedMenuList,
                disablePadding: true,
                autoFocusItem: false,
              },
            }}
            sx={{
              '& .MuiSelect-select': {
                padding: '0.25rem 0.5rem',
              },
              borderColor: color || 'rgba(110, 110, 110, 0.5)',
              borderRadius: '4px',
              transition: 'all 0.2s ease',
              '& .MuiOutlinedInput-notchedOutline': {
                border: '1px solid',
                borderColor: color || 'rgba(110, 110, 110, 0.5)',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: color || ' #1c1c1c',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: '1px solid',
                borderColor: color || ' #1c1c1c',
              },
              '& .MuiSelect-icon': {
                color: color,
              },
            }}
          >
            <MenuItem
              value={'(All)'}
              sx={{
                padding: '0',
                minHeight: `${MENU_ITEM_HEIGHT}px`,
                marginY: 0,
                '&.Mui-selected': {
                  backgroundColor: 'rgba(132, 132, 132, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(132, 132, 132, 0.05)',
                  },
                },
                transition: 'all 0.1s ease',
              }}
            >
              <FormControlLabel
                control={
                  <MuiCheckbox
                    checked={checkboxValues.length === options.length + 1}
                    sx={{
                      '&.Mui-checked': {
                        color: '#404040',
                      },
                      width: '100%',
                      overflow: 'hidden',
                      padding: '4px',
                    }}
                    size="small"
                  />
                }
                label={'(All)'}
                sx={{
                  margin: 0,
                  '& .MuiFormControlLabel-label': {
                    fontSize: fontSize.extraSmall,
                    color: '#2d3748',
                    fontWeight: '500',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </MenuItem>
            {options?.map((option: any) => (
              <MenuItem
                key={option}
                value={option}
                sx={{
                  padding: '0',
                  minHeight: `${MENU_ITEM_HEIGHT}px`,
                  marginY: 0,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(132, 132, 132, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(132, 132, 132, 0.05)',
                    },
                  },
                  transition: 'all 0.1s ease',
                }}
              >
                <FormControlLabel
                  control={
                    <MuiCheckbox
                      checked={
                        checkboxValues.includes(option) ||
                        checkboxValues.some((v: any) => v == option)
                      }
                      sx={{
                        '&.Mui-checked': {
                          color: '#404040',
                        },
                        width: '100%',
                        overflow: 'hidden',
                        padding: '4px',
                      }}
                      size="small"
                    />
                  }
                  label={option === '' ? '(Empty)' : option === null ? '(Blank)' : option}
                  sx={{
                    margin: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: fontSize.extraSmall,
                      color: '#2d3748',
                      fontWeight: '500',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Box>
  );
};

const ChipDisplay = memo(({ value, parameter }: any) => {
  return (
    <Chip
      key={value}
      label={parameter.columns?.dataType === 'date' ? new Date(value).toLocaleDateString() : value}
      size="small"
      sx={{
        backgroundColor: '#f0f4f8',
        fontSize: fontSize.extraSmall,
      }}
    />
  );
});

export default Checkbox;
